import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import ReactFlow, {ReactFlowInstance, useEdgesState, useNodesState, Node, Edge, OnConnect} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {
  HandlesContext,
  HandlesContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/HandlesContext';
import {
  TaskSchemaStaticNodeData,
  TaskSchemaNodeData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {isNewEdgeValid} from 'processes/modals/utils/TransitionModalValidations';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
  sizeToWidth,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar, {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import buildFlowchartFromTransitionData from './buildFlowchartFromTransitionData';
import {getEdgeTypeFromHandleId} from './flowchartIdentifierUtils';
import {
  READ_ONLY_FLOWCHART_PROPS,
  EDITABLE_FLOWCHART_PROPS,
  DEFAULT_FLOWCHART_PROPS,
  TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_STYLES,
  FlowchartStaticEdgeData,
} from './TransitionModalWithFlowchartEditorConstants';
import {
  TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_NODE_TYPES,
  TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_EDGE_TYPES,
} from './TransitionModalWithFlowchartEditorRegistry';

const LEFT_SIDEPANEL_SIZE = SidepanelSize.MEDIUM;
const LEFT_SIDEPANEL_WIDTH = sizeToWidth[LEFT_SIDEPANEL_SIZE] as number;

interface TransitionModalWithFlowchartEditorBodyProps {
  initialSourceNodesData: Array<TaskSchemaStaticNodeData>;
  initialTargetNodeData: TaskSchemaStaticNodeData | null;
  initialEdgesData: Array<FlowchartStaticEdgeData>;
  disabled?: boolean;
  onChange: ({
    nodesData,
    edgesData,
  }: {
    nodesData: Array<TaskSchemaStaticNodeData>;
    edgesData: Array<FlowchartStaticEdgeData>;
  }) => void;
}

interface LeftSidepanelContent {
  icon: BenchlingIconName;
  name: string;
  content: JSX.Element;
}

/**
 * The flowchart sidebar that displays the left sidepanel content.
 *
 * Responsible for flowchart-related settings and content.
 */
function LeftSidepanel(props: {isOpen: boolean; setIsOpen: (isOpen: boolean) => void}): JSX.Element {
  const [leftSidepanelContent, setLeftSidepanelContent] = useState<LeftSidepanelContent | null>(null);
  const [selectedSidebarIconIndex, setSelectedSidebarIconIndex] = useState<number | null>(null);

  return (
    <div className={css(styles.leftSidepanelContainer)}>
      <FlowchartSidebar
        icons={[
          {
            name: 'process_task_schema',
            onClick: () => {
              setLeftSidepanelContent({
                icon: 'process_task_schema',
                name: 'Add Nodes',
                content: <div>Add Nodes content</div>,
              });
              props.setIsOpen(true);
            },
            tooltip: 'Add Nodes',
          },
        ]}
        selectedIconIndex={selectedSidebarIconIndex}
        setSelectedIconIndex={setSelectedSidebarIconIndex}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        size={LEFT_SIDEPANEL_SIZE}
        sidepanelName={leftSidepanelContent?.name ?? ''}
        sidepanelIcon={leftSidepanelContent?.icon ?? 'wand-magic-sparkles'}
        isOpen={props.isOpen}
        onClose={() => {
          props.setIsOpen(false);
          setLeftSidepanelContent(null);
          setSelectedSidebarIconIndex(null);
        }}
      >
        {leftSidepanelContent?.content}
      </FlowchartContextPanel>
    </div>
  );
}

/**
 * A sidepanel component that displays the details of a selected node
 */
function RightSidepanel(props: {
  selectedNode: Node | null;
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  const {selectedNode, isOpen, onClose} = props;
  return (
    <FlowchartContextPanel
      panelSide={PanelSide.RIGHT}
      sidepanelName={`Node ${selectedNode?.data.nodeName} Details`}
      sidepanelIcon="settings"
      isOpen={isOpen}
      onClose={onClose}
      size={SidepanelSize.MEDIUM}
    >
      {selectedNode && <div>{selectedNode.data.nodeDetailsSidepanelContent(selectedNode)}</div>}
    </FlowchartContextPanel>
  );
}

export default function TransitionModalWithFlowchartEditorBody(
  props: TransitionModalWithFlowchartEditorBodyProps
): JSX.Element {
  // Left Sidepanel state
  const [isLeftSidepanelOpen, setIsLeftSidepanelOpen] = useState(false);

  // Right Sidepanel state
  const [isRightSidepanelOpen, setIsRightSidepanelOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const openSidepanelForNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setIsRightSidepanelOpen(true);
  }, []);

  const handleRightSidepanelClose = useCallback(() => {
    setIsRightSidepanelOpen(false);
    setSelectedNodeId(null);
  }, []);

  const {initialNodes, initialEdges} = useMemo((): {
    initialNodes: Array<Node<TaskSchemaNodeData>>;
    initialEdges: Array<Edge>;
  } => {
    const {sourceNodes, targetNode, edges} = buildFlowchartFromTransitionData({
      sourceNodesStaticData: props.initialSourceNodesData,
      targetNodeStaticData: props.initialTargetNodeData,
      edgesData: props.initialEdgesData,
      options: {
        onNodeHeaderClick: openSidepanelForNode,
        // TODO: Properly wire up field click, this is a placeholder for now
        onFieldClick: openSidepanelForNode,
      },
    });
    return {
      initialNodes: sourceNodes.concat(targetNode ?? []),
      initialEdges: edges,
    };
  }, [
    props.initialSourceNodesData,
    props.initialTargetNodeData,
    props.initialEdgesData,
    openSidepanelForNode,
  ]);

  const [nodes, _, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const interactionProps = props.disabled ? READ_ONLY_FLOWCHART_PROPS : EDITABLE_FLOWCHART_PROPS;

  const onConnect: OnConnect = useCallback(
    (params) => {
      if (!isNewEdgeValid(params, edges)) {
        return;
      }

      const newEdge = {
        id: uuidv4(),
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: getEdgeTypeFromHandleId(params.sourceHandle!), // sourceHandle is guaranteed to be non-null by isNewEdgeValid
      };

      setEdges((eds) => [...eds, newEdge]);
    },
    [edges, setEdges]
  );

  const handlesContext: HandlesContextValue = useMemo(
    () => ({
      handlesInUse: edges.flatMap((edge) => {
        if (!edge.sourceHandle || !edge.targetHandle) {
          throw new Error('Invalid edge: missing sourceHandle or targetHandle');
        }
        return [edge.sourceHandle, edge.targetHandle];
      }),
    }),
    [edges]
  );

  useEffect(() => {
    const nodesData = nodes.map((node) => node.data);
    props.onChange({nodesData, edgesData: edges as Array<FlowchartStaticEdgeData>});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);

  return (
    <div className={css(TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_STYLES.reactFlowCanvas)}>
      <ReactFlowWrapper fullWidth={true}>
        <div style={{display: 'flex', height: '100%', position: 'relative'}}>
          <LeftSidepanel isOpen={isLeftSidepanelOpen} setIsOpen={setIsLeftSidepanelOpen} />
          <div
            style={{
              flexGrow: 1,
              marginLeft: FLOWCHART_SIDE_PANEL_WIDTH,
              position: 'relative',
            }}
          >
            <HandlesContext.Provider value={handlesContext}>
              <ReactFlow
                {...DEFAULT_FLOWCHART_PROPS}
                {...interactionProps}
                nodes={nodes}
                nodeTypes={TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_NODE_TYPES}
                edgeTypes={TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_EDGE_TYPES}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onInit={(reactFlowInstance: ReactFlowInstance) => {
                  reactFlowInstance.fitView();
                }}
                onConnect={onConnect}
              >
                <HorizontalControls shiftLeft={isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0} />
                <DefaultBackground />
              </ReactFlow>
            </HandlesContext.Provider>
          </div>
          <RightSidepanel
            selectedNode={selectedNodeId ? (nodes.find((node) => node.id === selectedNodeId) ?? null) : null}
            isOpen={isRightSidepanelOpen}
            onClose={handleRightSidepanelClose}
          />
        </div>
      </ReactFlowWrapper>
    </div>
  );
}

const styles = StyleSheet.create({
  leftSidepanelContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    zIndex: 10,
  },
});
